import { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useCompany, PageHeader, Money } from "@/components/Shared";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, Minus, Trash2, Search, ShoppingCart, Printer, Loader2, X } from "lucide-react";

export default function POS() {
  const { companyId, cur } = useCompany();
  const [products, setProducts] = useState([]);
  const [q, setQ] = useState("");
  const [cart, setCart] = useState([]);
  const [pays, setPays] = useState([{ method: "cash", provider: "orange_money", amount: 0 }]);
  const [receipt, setReceipt] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => { if (companyId) api.get("/products", { params: { company_id: companyId } }).then(({ data }) => setProducts(data.products)); }, [companyId]);

  const filtered = products.filter((p) => p.name.toLowerCase().includes(q.toLowerCase()));
  const addToCart = (p) => {
    setCart((c) => { const f = c.find((x) => x.product_id === p.id);
      return f ? c.map((x) => x.product_id === p.id ? { ...x, qty: x.qty + 1 } : x)
               : [...c, { product_id: p.id, name: p.name, unit_price: p.sale_price, tax_rate: 0, qty: 1 }]; });
  };
  const setQty = (i, d) => setCart((c) => c.map((x, idx) => idx === i ? { ...x, qty: Math.max(1, x.qty + d) } : x));
  const removeItem = (i) => setCart((c) => c.filter((_, idx) => idx !== i));
  const total = cart.reduce((s, l) => s + l.qty * l.unit_price, 0);
  const paid = pays.reduce((s, p) => s + (parseFloat(p.amount) || 0), 0);
  const change = Math.max(paid - total, 0);

  const updatePay = (i, k, v) => setPays(pays.map((p, idx) => idx === i ? { ...p, [k]: v } : p));
  const addPay = () => setPays([...pays, { method: "mobile_money", provider: "orange_money", amount: 0 }]);
  const removePay = (i) => setPays(pays.filter((_, idx) => idx !== i));
  const fillRemaining = (i) => updatePay(i, "amount", Math.max(total - (paid - (parseFloat(pays[i].amount) || 0)), 0));

  const checkout = async () => {
    if (cart.length === 0) return toast.error("Panier vide");
    if (paid < total) return toast.error("Paiement insuffisant");
    setSaving(true);
    try {
      const body = { lines: cart, payments: pays.filter((p) => parseFloat(p.amount) > 0).map((p) => ({ method: p.method, provider: p.method === "mobile_money" ? p.provider : null, amount: parseFloat(p.amount) })) };
      const { data } = await api.post("/pos/sale", body, { params: { company_id: companyId } });
      setReceipt({ ...data, cart, total, paid, change });
      setCart([]); setPays([{ method: "cash", provider: "orange_money", amount: 0 }]);
      api.get("/products", { params: { company_id: companyId } }).then(({ data }) => setProducts(data.products));
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); } finally { setSaving(false); }
  };

  return (
    <div data-testid="pos-page">
      <PageHeader title="POS / Caisse" subtitle="Vente rapide, paiement mixte, reçu imprimable." />
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 space-y-3">
          <div className="relative"><Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Rechercher un produit…" className="pl-9 rounded-full" data-testid="pos-search-input" /></div>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
            {filtered.map((p) => (
              <button key={p.id} onClick={() => addToCart(p)} className="p-3 rounded-md border border-border bg-white text-left hover:border-primary active:scale-95 transition-all" data-testid={`pos-product-${p.id}`}>
                <div className="font-medium text-sm truncate">{p.name}</div>
                <div className="text-primary font-display font-bold text-sm mt-1"><Money value={p.sale_price} cur={cur} /></div>
                <div className="text-xs text-muted-foreground">Stock: {p.quantity}</div>
              </button>))}
          </div>
        </div>

        <Card className="p-4 rounded-md border-border shadow-sm h-fit sticky top-20" data-testid="pos-cart">
          <div className="flex items-center gap-2 font-display font-semibold mb-3"><ShoppingCart className="w-5 h-5 text-primary" /> Panier</div>
          {cart.length === 0 ? <p className="text-sm text-muted-foreground py-6 text-center">Ajoutez des produits.</p>
            : <div className="space-y-2 mb-3 max-h-52 overflow-auto">
                {cart.map((l, i) => (
                  <div key={i} className="flex items-center gap-2 text-sm">
                    <div className="flex-1 truncate">{l.name}</div>
                    <button onClick={() => setQty(i, -1)} className="w-6 h-6 rounded border border-border" data-testid={`pos-minus-${i}`}><Minus className="w-3 h-3 mx-auto" /></button>
                    <span className="w-6 text-center">{l.qty}</span>
                    <button onClick={() => setQty(i, 1)} className="w-6 h-6 rounded border border-border" data-testid={`pos-plus-${i}`}><Plus className="w-3 h-3 mx-auto" /></button>
                    <span className="w-20 text-right tabular">{<Money value={l.qty * l.unit_price} cur={cur} />}</span>
                    <button onClick={() => removeItem(i)}><Trash2 className="w-3.5 h-3.5 text-rose-500" /></button>
                  </div>))}
              </div>}
          <div className="flex justify-between font-display font-bold text-lg border-t border-border pt-2"><span>Total</span><Money value={total} cur={cur} /></div>

          <div className="mt-3 space-y-2">
            <div className="text-xs font-semibold text-muted-foreground">Paiement</div>
            {pays.map((p, i) => (
              <div key={i} className="flex gap-1.5 items-center">
                <select className="h-8 rounded-md border border-input bg-background px-1 text-xs" value={p.method} onChange={(e) => updatePay(i, "method", e.target.value)} data-testid={`pos-pay-method-${i}`}>
                  <option value="cash">Espèces</option><option value="bank">Banque</option><option value="mobile_money">MoMo</option>
                </select>
                {p.method === "mobile_money" && <select className="h-8 rounded-md border border-input bg-background px-1 text-xs" value={p.provider} onChange={(e) => updatePay(i, "provider", e.target.value)}>
                  <option value="orange_money">Orange</option><option value="moov_money">Moov</option><option value="wave_money">Wave</option></select>}
                <Input type="number" className="h-8 flex-1" value={p.amount} onChange={(e) => updatePay(i, "amount", e.target.value)} onFocus={() => !p.amount && fillRemaining(i)} data-testid={`pos-pay-amount-${i}`} />
                {pays.length > 1 && <button onClick={() => removePay(i)}><X className="w-3.5 h-3.5 text-muted-foreground" /></button>}
              </div>))}
            <Button variant="outline" size="sm" className="rounded-full w-full" onClick={addPay} data-testid="pos-add-payment"><Plus className="w-3 h-3 mr-1" />Ajouter un moyen</Button>
          </div>
          <div className="flex justify-between text-sm mt-2"><span className="text-muted-foreground">Encaissé</span><Money value={paid} cur={cur} /></div>
          {change > 0 && <div className="flex justify-between text-sm text-emerald-600 font-semibold"><span>Monnaie à rendre</span><Money value={change} cur={cur} /></div>}
          <Button onClick={checkout} disabled={saving} className="w-full rounded-full mt-3 active:scale-95 transition-transform" data-testid="pos-checkout-button">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Encaisser la vente"}
          </Button>
        </Card>
      </div>

      <Dialog open={!!receipt} onOpenChange={(o) => !o && setReceipt(null)}>
        <DialogContent className="bg-popover max-w-sm" data-testid="pos-receipt">
          <DialogHeader><DialogTitle className="font-display text-center">Reçu {receipt?.invoice?.number}</DialogTitle></DialogHeader>
          {receipt && <div id="receipt" className="text-sm font-mono">
            <div className="text-center font-bold mb-2">VENTE VALIDÉE</div>
            {receipt.cart.map((l, i) => <div key={i} className="flex justify-between"><span>{l.qty}× {l.name}</span><Money value={l.qty * l.unit_price} cur={cur} /></div>)}
            <div className="border-t border-dashed border-border my-2" />
            <div className="flex justify-between font-bold"><span>TOTAL</span><Money value={receipt.total} cur={cur} /></div>
            <div className="flex justify-between"><span>Encaissé</span><Money value={receipt.paid} cur={cur} /></div>
            <div className="flex justify-between"><span>Monnaie</span><Money value={receipt.change} cur={cur} /></div>
            <div className="text-center text-xs text-muted-foreground mt-3">Merci de votre visite — NATORIQ</div>
          </div>}
          <Button onClick={() => window.print()} className="rounded-full" data-testid="print-receipt-button"><Printer className="w-4 h-4 mr-1" /> Imprimer</Button>
        </DialogContent>
      </Dialog>
    </div>
  );
}
